// ============================================================
// frontend/src/views/ClientDetailView.tsx
// ============================================================
// Client detail with three tabs:
//   - Sessions: upcoming + past lists
//   - Packages: package cards with payment progress + history
//   - Background: presenting concern + intake notes
//
// Plus modals for:
//   - Edit client / delete client
//   - Assign package (3-step flow: template → overrides → scheduling)
//   - One-off session (creates implicit 1-session package)
//   - Schedule additional session against active package
//   - Edit existing package
//   - Record payment
//
// Local state updates optimistically; full refetch only on package
// assignment (which touches sessions + payments).
// ============================================================

import { useEffect, useState } from 'react';
import {
  ArrowLeft, ChevronRight, Edit2, Trash2, Plus,
  Layers, Zap, AlertCircle, DollarSign,
} from 'lucide-react';
import { api } from '../lib/api';
import { useSaving } from '../lib/useSaving';
import {
  formatDate, formatDateTime, isPast, fromDateTimeLocal,
} from '../lib/format';
import {
  Modal, ModalFooter, Field, StatusPill, PaymentPill,
} from '../components/UI';
import { SaveButton } from '../components/SaveButton';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import { ClientForm } from './ClientsView';
import type {
  ClientDetailResponse, ClientPackageWithStats, Session, Payment,
  Client, PackageTemplate, TemplateListResponse, Settings,
  AssignPackageRequest, OneOffRequest, CreatePaymentRequest, SchedulingMode,
} from '@consilium/shared';
import type { ViewState } from '../App';

interface ClientDetailViewProps {
  clientId: string;
  setView: (v: ViewState) => void;
  showError: (msg: string) => void;
  settings: Settings | null;
}

export function ClientDetailView({ clientId, setView, showError, settings }: ClientDetailViewProps) {
  const [data, setData] = useState<ClientDetailResponse | null>(null);
  const [tab, setTab] = useState<'sessions' | 'packages' | 'background'>('sessions');

  // Modal/dialog state
  const [showEdit, setShowEdit] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showOneOff, setShowOneOff] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [editPackage, setEditPackage] = useState<ClientPackageWithStats | null>(null);
  const [paymentForPkg, setPaymentForPkg] = useState<ClientPackageWithStats | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [completingPkgId, setCompletingPkgId] = useState<string | null>(null);

  // Load full data
  const refresh = async () => {
    try {
      const d = await api.get<ClientDetailResponse>(`/clients/${clientId}`);
      setData(d);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load client');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<ClientDetailResponse>(`/clients/${clientId}`);
        if (!cancelled) setData(d);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load client');
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (!data) return <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><ConsiliumLoader size={48} /></div>;

  const { client, packages, sessions, payments } = data;
  const activePkg = packages.find((p) => p.status === 'active' && !p.isOneOff);
  const hasAnyActive = packages.some((p) => p.status === 'active');

  // Split sessions for the Sessions tab
  const upcomingSessions = sessions.filter((s) => !isPast(s.scheduledAt) && s.status === 'scheduled');
  const pastSessions = sessions.filter((s) => s.status === 'completed' || (isPast(s.scheduledAt) && s.status !== 'scheduled'));

  // Handlers
  const handleClientUpdated = (c: Client) => {
    setData({ ...data, client: c });
    setShowEdit(false);
  };

  const handleDelete = async () => {
    try {
      await api.del(`/clients/${clientId}`);
      setView({ name: 'clients' });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const completePackage = async (pkgId: string) => {
    if (!confirm('Mark this package as completed?')) return;
    setCompletingPkgId(pkgId);
    try {
      await api.post(`/packages/${pkgId}/complete`);
      await refresh();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to complete');
    } finally {
      setCompletingPkgId(null);
    }
  };

  const deletePackage = async (pkgId: string) => {
    if (!confirm('Delete this package? Its sessions will also be removed.')) return;
    try {
      await api.del(`/packages/${pkgId}`);
      await refresh();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="fade-in">
      <button
        className="btn-icon"
        onClick={() => setView({ name: 'clients' })}
        style={{ marginBottom: 16, fontSize: 13, padding: '6px 10px', minWidth: 0 }}
      >
        <ArrowLeft size={14} style={{ marginRight: 6 }} />Back to clients
      </button>

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="mono-cap">Client</div>
          <h1 className="page-title">{client.name}</h1>
          {(client.email || client.phone) && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>
              {client.email}{client.email && client.phone && ' · '}{client.phone}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn-icon" onClick={() => setShowEdit(true)} title="Edit"><Edit2 size={15} /></button>
          <button className="btn-icon" onClick={() => setConfirmDelete(true)} title="Delete"><Trash2 size={15} /></button>
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn-primary"
          onClick={() => setShowAssign(true)}
          disabled={hasAnyActive}
          title={hasAnyActive ? 'Complete the current active package first' : ''}
        >
          <Layers size={16} />Assign package
        </button>
        <button
          className="btn-ghost"
          onClick={() => setShowOneOff(true)}
          disabled={hasAnyActive}
          title={hasAnyActive ? 'Complete the current active package first' : ''}
        >
          <Zap size={16} />One-off session
        </button>
        {activePkg && (
          <button className="btn-ghost" onClick={() => setShowSchedule(true)}>
            <Plus size={16} />Schedule session
          </button>
        )}
      </div>

      {hasAnyActive && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
          <AlertCircle size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Only one active package per client at a time.
        </div>
      )}

      {client.presenting && (
        <div className="card" style={{ marginTop: 20, background: 'var(--bg-overlay)' }}>
          <div className="label">Presenting concern</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{client.presenting}</div>
        </div>
      )}

      <div className="tabs" style={{ marginTop: 28 }}>
        {(['sessions', 'packages', 'background'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`tab ${tab === t ? 'active' : ''}`}
            style={{ textTransform: 'capitalize' }}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 24 }}>
        {tab === 'sessions' && (
          <SessionsTab
            sessions={sessions}
            upcomingSessions={upcomingSessions}
            pastSessions={pastSessions}
            packages={packages}
            setView={setView}
          />
        )}
        {tab === 'packages' && (
          <PackagesTab
            packages={packages}
            payments={payments}
            onEditPackage={setEditPackage}
            onDeletePackage={deletePackage}
            onCompletePackage={completePackage}
            onAddPayment={setPaymentForPkg}
            onPaymentDeleted={refresh}
            completingPkgId={completingPkgId}
            showError={showError}
          />
        )}
        {tab === 'background' && (
          <BackgroundTab client={client} />
        )}
      </div>

      {/* Modals */}
      {showEdit && <ClientForm initial={client as Client} onUpdated={handleClientUpdated} onCancel={() => setShowEdit(false)} showError={showError} />}
      {showAssign && <AssignPackageFlow clientId={clientId} settings={settings} onAssigned={() => { setShowAssign(false); refresh(); }} onCancel={() => setShowAssign(false)} showError={showError} />}
      {showOneOff && <OneOffForm clientId={clientId} settings={settings} onCreated={() => { setShowOneOff(false); refresh(); }} onCancel={() => setShowOneOff(false)} showError={showError} />}
      {showSchedule && activePkg && <ScheduleForm clientId={clientId} pkg={activePkg} settings={settings} onCreated={() => { setShowSchedule(false); refresh(); }} onCancel={() => setShowSchedule(false)} showError={showError} />}
      {editPackage && <EditPackageForm pkg={editPackage} onSaved={() => { setEditPackage(null); refresh(); }} onCancel={() => setEditPackage(null)} showError={showError} />}
      {paymentForPkg && <PaymentForm pkg={paymentForPkg} onCreated={() => { setPaymentForPkg(null); refresh(); }} onCancel={() => setPaymentForPkg(null)} showError={showError} />}
      {confirmDelete && (
        <Modal title="Delete client?" onClose={() => setConfirmDelete(false)}>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            This will permanently delete <strong>{client.name}</strong> and all their packages, sessions, and payments. This cannot be undone.
          </div>
          <ModalFooter>
            <button className="btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn-danger" onClick={handleDelete}>Delete permanently</button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// SessionsTab
// ============================================================
interface SessionsTabProps {
  sessions: Session[];
  upcomingSessions: Session[];
  pastSessions: Session[];
  packages: ClientPackageWithStats[];
  setView: (v: ViewState) => void;
}

function SessionsTab({ sessions, upcomingSessions, pastSessions, packages, setView }: SessionsTabProps) {
  if (sessions.length === 0) {
    return (
      <div className="card empty-state">
        <div style={{ fontSize: 14 }}>No sessions yet. Assign a package or schedule a one-off.</div>
      </div>
    );
  }

  return (
    <div>
      {upcomingSessions.length > 0 && (
        <>
          <div className="label">Upcoming</div>
          <div className="card" style={{ padding: 0, marginBottom: 18 }}>
            {upcomingSessions.map((s) => (
              <SessionRow key={s.id} session={s}
                pkg={packages.find((p) => p.id === s.packageId)}
                onClick={() => setView({ name: 'session', sessionId: s.id })} />
            ))}
          </div>
        </>
      )}
      {pastSessions.length > 0 && (
        <>
          <div className="label">Past sessions</div>
          <div className="card" style={{ padding: 0 }}>
            {pastSessions.slice(0, 50).map((s) => (
              <SessionRow key={s.id} session={s}
                pkg={packages.find((p) => p.id === s.packageId)}
                onClick={() => setView({ name: 'session', sessionId: s.id })} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SessionRow({ session, pkg, onClick }: { session: Session; pkg?: ClientPackageWithStats; onClick: () => void }) {
  return (
    <div className="list-row" onClick={onClick}>
      <div className="row-main">
        <div className="row-primary">{formatDateTime(session.scheduledAt)}</div>
        <div className="row-secondary">{session.agenda || (pkg ? pkg.name : '—')}</div>
      </div>
      <div className="row-meta">
        {pkg?.isOneOff && <span className="pill pill-oneoff">One-off</span>}
        <StatusPill status={session.status} />
        <ChevronRight size={18} color="var(--muted-strong)" />
      </div>
    </div>
  );
}

// ============================================================
// PackagesTab
// ============================================================
interface PackagesTabProps {
  packages: ClientPackageWithStats[];
  payments: Payment[];
  onEditPackage: (p: ClientPackageWithStats) => void;
  onDeletePackage: (id: string) => void;
  onCompletePackage: (id: string) => void;
  onAddPayment: (p: ClientPackageWithStats) => void;
  onPaymentDeleted: () => void;
  completingPkgId: string | null;
  showError: (msg: string) => void;
}

function PackagesTab({ packages, payments, onEditPackage, onDeletePackage, onCompletePackage, onAddPayment, onPaymentDeleted, completingPkgId, showError }: PackagesTabProps) {
  if (packages.length === 0) {
    return <div className="card empty-state"><div style={{ fontSize: 14 }}>No packages yet.</div></div>;
  }

  return (
    <div className="stack">
      {packages.map((pkg) => (
        <PackageCard
          key={pkg.id} pkg={pkg}
          payments={payments.filter((p) => p.packageId === pkg.id)}
          onEdit={() => onEditPackage(pkg)}
          onDelete={() => onDeletePackage(pkg.id)}
          onComplete={() => onCompletePackage(pkg.id)}
          onAddPayment={() => onAddPayment(pkg)}
          onPaymentDeleted={onPaymentDeleted}
          completing={completingPkgId === pkg.id}
          showError={showError}
        />
      ))}
    </div>
  );
}

interface PackageCardProps {
  pkg: ClientPackageWithStats;
  payments: Payment[];
  onEdit: () => void;
  onDelete: () => void;
  onComplete: () => void;
  onAddPayment: () => void;
  onPaymentDeleted: () => void;
  completing: boolean;
  showError: (msg: string) => void;
}

function PackageCard({ pkg, payments, onEdit, onDelete, onComplete, onAddPayment, onPaymentDeleted, completing, showError }: PackageCardProps) {
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(null);

  const deletePayment = async (paymentId: string) => {
    if (!confirm('Delete this payment?')) return;
    setDeletingPaymentId(paymentId);
    try {
      await api.del(`/packages/payments/${paymentId}`);
      onPaymentDeleted();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete payment');
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const paid = pkg.paidTotal;
  const total = pkg.totalFee;
  const progressPct = Math.min(100, (paid / total) * 100);

  return (
    <div className="card">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div className="display" style={{ fontSize: 17, fontWeight: 600 }}>{pkg.name}</div>
            <PaymentPill pkg={pkg} />
            {pkg.isOneOff && <span className="pill pill-oneoff">One-off</span>}
            {pkg.status === 'active' && !pkg.isOneOff && <span className="pill pill-active-pkg">Active</span>}
            {pkg.status !== 'active' && <span className="pill pill-cancelled">{pkg.status}</span>}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            Started {formatDate(pkg.startDate)} · {pkg.completedSessions} completed / {pkg.totalSessions} total
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {pkg.status === 'active' && (
            <button
              className="btn-ghost"
              style={{ padding: '6px 12px', fontSize: 12, minHeight: 32 }}
              onClick={onComplete}
              disabled={completing}
            >
              {completing ? 'Saving…' : 'Mark complete'}
            </button>
          )}
          <button className="btn-icon" onClick={onEdit}><Edit2 size={14} /></button>
          <button className="btn-icon" onClick={onDelete}><Trash2 size={14} /></button>
        </div>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: 'var(--bg-overlay)', borderRadius: 12 }}>
        <div className="row" style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
            <strong>{paid.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong> {pkg.currency}
          </div>
          <button
            className="btn-ghost"
            style={{ padding: '6px 12px', fontSize: 12, minHeight: 32 }}
            onClick={onAddPayment}
          >
            <DollarSign size={12} />Record payment
          </button>
        </div>
        <div className="progress-track">
          <div
            className={`progress-fill ${paid >= total ? 'is-paid' : 'is-partial'}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {payments.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
            {payments.map((p, i) => (
              <div key={p.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none',
              }}>
                <span>{formatDate(p.paidOn)} · {p.method || p.note || 'Payment'}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{p.amount.toLocaleString()}</span>
                  <button
                    onClick={() => deletePayment(p.id)}
                    disabled={deletingPaymentId === p.id}
                    style={{ background: 'none', border: 'none', color: 'var(--muted-strong)', cursor: 'pointer', padding: 2 }}
                    title="Delete payment"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pkg.notes && (
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {pkg.notes}
        </div>
      )}
    </div>
  );
}

// ============================================================
// BackgroundTab
// ============================================================
function BackgroundTab({ client }: { client: ClientDetailResponse['client'] }) {
  return (
    <div className="card">
      <div className="label">Date of birth</div>
      <div style={{ marginBottom: 16, fontSize: 14 }}>
        {client.dob ? formatDate(client.dob) : <span style={{ color: 'var(--muted)' }}>Not recorded</span>}
      </div>
      <div className="label">Background / intake notes</div>
      <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        {client.background || <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>No background notes yet.</span>}
      </div>
    </div>
  );
}

// ============================================================
// AssignPackageFlow — 3-step modal: pick template → overrides → schedule
// ============================================================
interface AssignPackageFlowProps {
  clientId: string;
  settings: Settings | null;
  onAssigned: () => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}

function AssignPackageFlow({ clientId, settings, onAssigned, onCancel, showError }: AssignPackageFlowProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [templates, setTemplates] = useState<PackageTemplate[]>([]);
  const [selected, setSelected] = useState<PackageTemplate | null>(null);
  const [overrides, setOverrides] = useState<AssignPackageRequest['overrides']>({
    name: '', totalSessions: 8, totalFee: 0, currency: 'PKR',
    startDate: new Date().toISOString().slice(0, 10), notes: '',
  });
  const [scheduling, setScheduling] = useState<AssignPackageRequest['scheduling']>({ mode: 'later' });
  const { saving, saved, run } = useSaving();

  useEffect(() => {
    (async () => {
      try {
        const { templates } = await api.get<TemplateListResponse>('/templates');
        setTemplates(templates.filter((t) => !t.archived));
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load templates');
      }
    })();
  }, []);

  const pickTemplate = (t: PackageTemplate) => {
    setSelected(t);
    // Snapshot the template into the override fields
    setOverrides({
      name: t.name,
      totalSessions: t.totalSessions,
      totalFee: t.totalFee,
      currency: t.currency,
      startDate: new Date().toISOString().slice(0, 10),
      notes: t.notes,
    });
    setStep(2);
  };

  const submit = async () => {
    try {
      await run(async () => {
        const payload: AssignPackageRequest = {
          clientId,
          templateId: selected?.id || null,
          overrides,
          scheduling: {
            ...scheduling,
            durationMin: settings?.defaultDurationMin || 50,
          },
        };
        await api.post('/packages/assign', payload);
      });
      setTimeout(onAssigned, 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to assign');
    }
  };

  return (
    <Modal title={`Assign package · Step ${step} of 3`} onClose={onCancel} wide>
      {step === 1 && (
        <div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
            Choose a template. Values are copied — you can adjust them next.
          </div>
          {templates.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: 14 }}>No templates yet. Create one from the Packages page.</div>
            </div>
          ) : (
            <div className="stack">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => pickTemplate(t)}
                  style={{
                    textAlign: 'left', background: 'var(--bg-elevated)',
                    border: '1px solid var(--border)', borderRadius: 14,
                    padding: 16, cursor: 'pointer', color: 'var(--ink)',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: 12,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="display" style={{ fontSize: 16, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                      {t.totalSessions} sessions · {t.totalFee.toLocaleString()} {t.currency}
                    </div>
                  </div>
                  <ChevronRight size={18} color="var(--muted-strong)" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 2 && selected && (
        <div>
          <div style={{ padding: 10, background: 'var(--bg-overlay)', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
            Starting from: <strong>{selected.name}</strong>. Changes here only affect this client.
          </div>
          <div className="stack-lg">
            <Field label="Package name">
              <input
                className="input" value={overrides.name}
                onChange={(e) => setOverrides({ ...overrides, name: e.target.value })}
              />
            </Field>
            <div className="grid-3">
              <Field label="Sessions">
                <input
                  type="number" min={1} className="input" value={overrides.totalSessions}
                  onChange={(e) => setOverrides({ ...overrides, totalSessions: Number(e.target.value) })}
                />
              </Field>
              <Field label="Total fee">
                <input
                  type="number" min={0} className="input" value={overrides.totalFee}
                  onChange={(e) => setOverrides({ ...overrides, totalFee: Number(e.target.value) })}
                />
              </Field>
              <Field label="Currency">
                <select
                  className="input" value={overrides.currency}
                  onChange={(e) => setOverrides({ ...overrides, currency: e.target.value })}
                >
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </select>
              </Field>
            </div>
            <Field label="Start date">
              <input
                type="date" className="input" value={overrides.startDate}
                onChange={(e) => setOverrides({ ...overrides, startDate: e.target.value })}
              />
            </Field>
            <Field label="Notes">
              <textarea
                className="input" value={overrides.notes || ''}
                onChange={(e) => setOverrides({ ...overrides, notes: e.target.value })}
              />
            </Field>
          </div>
          <ModalFooter>
            <button className="btn-ghost" onClick={() => setStep(1)}>← Back</button>
            <button className="btn-primary" onClick={() => setStep(3)}>Next →</button>
          </ModalFooter>
        </div>
      )}

      {step === 3 && (
        <SchedulingStep
          totalSessions={overrides.totalSessions}
          scheduling={scheduling}
          setScheduling={setScheduling}
          onBack={() => setStep(2)}
          onSubmit={submit}
          saving={saving}
          saved={saved}
        />
      )}
    </Modal>
  );
}

// ============================================================
// SchedulingStep (step 3 of assign flow)
// ============================================================
interface SchedulingStepProps {
  totalSessions: number;
  scheduling: AssignPackageRequest['scheduling'];
  setScheduling: (s: AssignPackageRequest['scheduling']) => void;
  onBack: () => void;
  onSubmit: () => void;
  saving: boolean;
  saved: boolean;
}

function SchedulingStep({ totalSessions, scheduling, setScheduling, onBack, onSubmit, saving, saved }: SchedulingStepProps) {
  const modes: { id: SchedulingMode; label: string; hint: string }[] = [
    { id: 'later', label: 'Skip for now', hint: 'Schedule sessions manually later.' },
    { id: 'first', label: 'Schedule first only', hint: 'Add just the first session.' },
    { id: 'all',   label: 'Schedule all sessions', hint: 'Bulk-schedule at a regular interval.' },
  ];
  const intervalPresets = [
    { days: 7,  label: 'Weekly' },
    { days: 14, label: 'Bi-weekly' },
    { days: 3,  label: 'Every 3 days' },
  ];

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
        How would you like to schedule sessions for this package?
      </div>
      <div className="stack-lg">
        <div className="stack" style={{ gap: 8 }}>
          {modes.map((m) => (
            <button
              key={m.id}
              onClick={() => setScheduling({ ...scheduling, mode: m.id })}
              style={{
                textAlign: 'left', padding: 14,
                background: scheduling.mode === m.id ? 'var(--bg-overlay)' : 'var(--bg-elevated)',
                border: `1px solid ${scheduling.mode === m.id ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 12, cursor: 'pointer', color: 'var(--ink)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>{m.label}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{m.hint}</div>
            </button>
          ))}
        </div>

        {(scheduling.mode === 'first' || scheduling.mode === 'all') && (
          <Field label="First session" required>
            <input
              type="datetime-local" className="input"
              value={scheduling.firstSessionAt ? scheduling.firstSessionAt.slice(0, 16) : ''}
              onChange={(e) => setScheduling({ ...scheduling, firstSessionAt: fromDateTimeLocal(e.target.value) })}
            />
          </Field>
        )}

        {scheduling.mode === 'all' && (
          <Field label="Interval between sessions">
            <div className="chip-row" style={{ marginBottom: 8 }}>
              {intervalPresets.map((p) => (
                <button
                  key={p.days}
                  className={`chip ${scheduling.intervalDays === p.days ? 'active' : ''}`}
                  onClick={() => setScheduling({ ...scheduling, intervalDays: p.days })}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Or custom:</span>
              <input
                type="number" min={1} className="input" style={{ width: 100 }}
                value={scheduling.intervalDays ?? 7}
                onChange={(e) => setScheduling({ ...scheduling, intervalDays: Number(e.target.value) })}
              />
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>days apart</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              Will create {totalSessions} sessions at this interval.
            </div>
          </Field>
        )}
      </div>

      <ModalFooter>
        <button className="btn-ghost" onClick={onBack} disabled={saving}>← Back</button>
        <SaveButton
          saving={saving} saved={saved} onClick={onSubmit}
          disabled={(scheduling.mode !== 'later') && !scheduling.firstSessionAt}
          savedText="Assigned"
        >
          Assign package
        </SaveButton>
      </ModalFooter>
    </div>
  );
}

// ============================================================
// OneOffForm
// ============================================================
interface OneOffFormProps {
  clientId: string;
  settings: Settings | null;
  onCreated: () => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}

function OneOffForm({ clientId, settings, onCreated, onCancel, showError }: OneOffFormProps) {
  const [data, setData] = useState<OneOffRequest>({
    clientId,
    scheduledAt: '',
    fee: settings?.defaultSessionRate || 5000,
    currency: settings?.defaultCurrency || 'PKR',
    durationMin: settings?.defaultDurationMin || 50,
    agenda: '',
    markPaid: false,
  });
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    if (!data.scheduledAt || !data.fee) return;
    try {
      await run(async () => {
        await api.post('/packages/one-off', data);
      });
      setTimeout(onCreated, 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create');
    }
  };

  return (
    <Modal title="One-off session" onClose={onCancel}>
      <div className="stack-lg">
        <Field label="When" required>
          <input
            type="datetime-local" className="input"
            value={data.scheduledAt ? data.scheduledAt.slice(0, 16) : ''}
            onChange={(e) => setData({ ...data, scheduledAt: fromDateTimeLocal(e.target.value) })}
          />
        </Field>
        <div className="grid-2">
          <Field label="Fee" required>
            <input
              type="number" min={0} className="input" value={data.fee}
              onChange={(e) => setData({ ...data, fee: Number(e.target.value) })}
            />
          </Field>
          <Field label="Currency">
            <select
              className="input" value={data.currency}
              onChange={(e) => setData({ ...data, currency: e.target.value })}
            >
              <option value="PKR">PKR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </Field>
        </div>
        <Field label="Agenda (optional)">
          <input
            className="input" value={data.agenda || ''}
            placeholder="What to focus on…"
            onChange={(e) => setData({ ...data, agenda: e.target.value })}
          />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={data.markPaid || false}
            onChange={(e) => setData({ ...data, markPaid: e.target.checked })}
            style={{ width: 18, height: 18 }}
          />
          Mark as paid
        </label>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton
          saving={saving} saved={saved} onClick={onSubmit}
          disabled={!data.scheduledAt || !data.fee}
        >
          Create session
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================
// ScheduleForm — add a session to an existing active package
// ============================================================
interface ScheduleFormProps {
  clientId: string;
  pkg: ClientPackageWithStats;
  settings: Settings | null;
  onCreated: () => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}

function ScheduleForm({ clientId, pkg, settings, onCreated, onCancel, showError }: ScheduleFormProps) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [agenda, setAgenda] = useState('');
  const [durationMin, setDurationMin] = useState(settings?.defaultDurationMin || 50);
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    if (!scheduledAt) return;
    try {
      await run(async () => {
        await api.post('/sessions', {
          clientId,
          packageId: pkg.id,
          scheduledAt,
          durationMin,
          agenda,
        });
      });
      setTimeout(onCreated, 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to schedule');
    }
  };

  return (
    <Modal title={`Schedule session · ${pkg.name}`} onClose={onCancel}>
      <div className="stack-lg">
        <Field label="When" required>
          <input
            type="datetime-local" className="input"
            value={scheduledAt ? scheduledAt.slice(0, 16) : ''}
            onChange={(e) => setScheduledAt(fromDateTimeLocal(e.target.value))}
          />
        </Field>
        <Field label="Duration (min)">
          <input
            type="number" min={15} step={5} className="input"
            value={durationMin}
            onChange={(e) => setDurationMin(Number(e.target.value))}
          />
        </Field>
        <Field label="Agenda (optional)">
          <input
            className="input" value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
          />
        </Field>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton saving={saving} saved={saved} onClick={onSubmit} disabled={!scheduledAt}>
          Schedule
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================
// EditPackageForm
// ============================================================
function EditPackageForm({ pkg, onSaved, onCancel, showError }: {
  pkg: ClientPackageWithStats;
  onSaved: () => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}) {
  const [data, setData] = useState({
    name: pkg.name,
    totalSessions: pkg.totalSessions,
    totalFee: pkg.totalFee,
    currency: pkg.currency,
    startDate: pkg.startDate,
    notes: pkg.notes,
  });
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    try {
      await run(async () => {
        await api.put(`/packages/${pkg.id}`, data);
      });
      setTimeout(onSaved, 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  return (
    <Modal title="Edit package" onClose={onCancel}>
      <div className="stack-lg">
        <Field label="Name">
          <input className="input" value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })} />
        </Field>
        <div className="grid-3">
          <Field label="Sessions">
            <input type="number" min={1} className="input" value={data.totalSessions}
              onChange={(e) => setData({ ...data, totalSessions: Number(e.target.value) })} />
          </Field>
          <Field label="Total fee">
            <input type="number" min={0} className="input" value={data.totalFee}
              onChange={(e) => setData({ ...data, totalFee: Number(e.target.value) })} />
          </Field>
          <Field label="Currency">
            <select className="input" value={data.currency}
              onChange={(e) => setData({ ...data, currency: e.target.value })}>
              <option value="PKR">PKR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </Field>
        </div>
        <Field label="Start date">
          <input type="date" className="input" value={data.startDate}
            onChange={(e) => setData({ ...data, startDate: e.target.value })} />
        </Field>
        <Field label="Notes">
          <textarea className="input" value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })} />
        </Field>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton saving={saving} saved={saved} onClick={onSubmit}>Save changes</SaveButton>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================
// PaymentForm
// ============================================================
function PaymentForm({ pkg, onCreated, onCancel, showError }: {
  pkg: ClientPackageWithStats;
  onCreated: () => void;
  onCancel: () => void;
  showError: (msg: string) => void;
}) {
  const remaining = pkg.totalFee - pkg.paidTotal;
  const [data, setData] = useState<CreatePaymentRequest>({
    amount: remaining > 0 ? remaining : 0,
    paidOn: new Date().toISOString().slice(0, 10),
    method: '',
    note: '',
  });
  const { saving, saved, run } = useSaving();

  const onSubmit = async () => {
    if (!data.amount || data.amount <= 0) return;
    try {
      await run(async () => {
        await api.post(`/packages/${pkg.id}/payments`, data);
      });
      setTimeout(onCreated, 600);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to record');
    }
  };

  return (
    <Modal title="Record payment" onClose={onCancel}>
      <div style={{ padding: 10, background: 'var(--bg-overlay)', borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
        <strong>{pkg.name}</strong> · Remaining: <strong>{remaining.toLocaleString()}</strong> {pkg.currency}
      </div>
      <div className="stack-lg">
        <Field label="Amount" required>
          <input
            type="number" min={0} className="input" value={data.amount}
            onChange={(e) => setData({ ...data, amount: Number(e.target.value) })}
          />
        </Field>
        <Field label="Paid on">
          <input
            type="date" className="input" value={data.paidOn}
            onChange={(e) => setData({ ...data, paidOn: e.target.value })}
          />
        </Field>
        <Field label="Method">
          <input
            className="input" value={data.method}
            placeholder="Cash, JazzCash, Bank transfer…"
            onChange={(e) => setData({ ...data, method: e.target.value })}
          />
        </Field>
        <Field label="Note">
          <input
            className="input" value={data.note}
            onChange={(e) => setData({ ...data, note: e.target.value })}
          />
        </Field>
      </div>
      <ModalFooter>
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <SaveButton
          saving={saving} saved={saved} onClick={onSubmit}
          disabled={!data.amount || data.amount <= 0}
          savedText="Recorded"
        >
          Record payment
        </SaveButton>
      </ModalFooter>
    </Modal>
  );
}
